/**
 * «Аккаунты на выключение» — кандидаты, не оправдавшие просмотров:
 *  - «В работе» ≥ N дней с получения статуса и ни один рилс не набрал X;
 *  - «Прогрев» ≥ N дней с первого рилса и ни один рилс не набрал Y.
 * Пороги настраиваются здесь же. Слева в каждой строке ✓ (подтвердить —
 * статус «Выключение») и ✗ (скрыть из списка на 24 часа).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '@wml-insta/shared';
import { api } from '../api/client.js';
import { useAccountsData } from '../lib/useAccountsData.js';
import { loadPersisted, savePersisted } from '../lib/persist.js';
import { formatNum } from '../lib/format.js';
import { AccountsTable } from '../ui/AccountsTable.js';
import { AccountFilters, EMPTY_FILTERS, filterAccounts, type AccountFiltersValue } from '../ui/AccountFilters.js';
import { PhoneTabs } from '../ui/PhoneTabs.js';

interface ShutdownConfig {
  workDays: number;
  workViews: number;
  warmupDays: number;
  warmupViews: number;
}

function ConfigBar({ onSaved }: { onSaved: () => void }) {
  const [cfg, setCfg] = useState<ShutdownConfig | null>(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api<ShutdownConfig>('/api/shutdown/config').then(setCfg).catch(() => {});
  }, []);

  if (!cfg) return null;

  const num = (key: keyof ShutdownConfig) => (
    <input
      type="number"
      className="admin-input"
      style={{ width: 90 }}
      min={1}
      value={cfg[key]}
      onChange={(e) => { setCfg({ ...cfg, [key]: Math.max(1, Number(e.target.value) || 1) }); setDirty(true); }}
    />
  );

  async function save() {
    if (!cfg || busy) return;
    setBusy(true);
    try {
      await api('/api/shutdown/config', { method: 'PATCH', body: JSON.stringify(cfg) });
      setDirty(false);
      onSaved();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <b>В работе:</b> из рилсов за последние {num('workDays')} дн. ни один не набрал {num('workViews')} просм.
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <b>Прогрев:</b> прошло {num('warmupDays')} дн. с первого рилса, и ни один рилс не набрал {num('warmupViews')} просм.
      </div>
      {dirty && (
        <button className="btn-primary" style={{ width: 'auto', padding: '0 18px' }} disabled={busy} onClick={save}>
          {busy ? '…' : 'Сохранить'}
        </button>
      )}
    </div>
  );
}

export function ShutdownPage({ me }: { me: User }) {
  const data = useAccountsData();
  const nav = useNavigate();
  const [ids, setIds] = useState<Set<string> | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [filters, setFiltersRaw] = useState<AccountFiltersValue>(
    () => ({ ...EMPTY_FILTERS, ...loadPersisted(me.id, 'filters.shutdown', {}) }),
  );
  const setFilters = (next: AccountFiltersValue) => {
    setFiltersRaw(next);
    savePersisted(me.id, 'filters.shutdown', next);
  };

  function loadCandidates() {
    api<{ ids: string[] }>('/api/shutdown/candidates').then((r) => setIds(new Set(r.ids))).catch(() => setIds(new Set()));
  }
  useEffect(loadCandidates, []);

  const candidates = useMemo(
    () => (ids ? data.accounts.filter((a) => ids.has(a.id)) : []),
    [data.accounts, ids],
  );
  const filtered = useMemo(() => filterAccounts(candidates, filters), [candidates, filters]);

  async function confirm(id: string) {
    setBusyId(id);
    try {
      await api(`/api/shutdown/${id}/confirm`, { method: 'POST' });
      const acc = data.accounts.find((a) => a.id === id);
      if (acc) data.applyPatched({ ...acc, status: 'Выключение' });
      setIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    } catch (e) {
      data.setError(e instanceof Error ? e.message : 'Не получилось');
    } finally {
      setBusyId(null);
    }
  }

  async function snooze(id: string) {
    setBusyId(id);
    try {
      await api(`/api/shutdown/${id}/snooze`, { method: 'POST' });
      setIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    } catch (e) {
      data.setError(e instanceof Error ? e.message : 'Не получилось');
    } finally {
      setBusyId(null);
    }
  }

  if (!data.loaded || ids == null) return <div className="empty-state">Загрузка…</div>;

  return (
    <div className="page-inner page-fill">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 12, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.02em' }}>Аккаунты на выключение</h1>
          <div style={{ color: 'var(--text-subtle)', fontSize: 13, marginTop: 4 }}>
            {formatNum(candidates.length)} кандидатов · ✓ — выключить · ✗ — скрыть на 24 часа
            <div style={{ marginTop: 3, fontSize: 12 }}>
              «В работе» проверяются по свежим рилсам (скользящее окно), «Прогрев» — с первого поста;
              акки без данных парсера в список не попадают
            </div>
            {filtered.length !== candidates.length && ` · показано ${formatNum(filtered.length)}`}
          </div>
        </div>
      </div>

      <ConfigBar onSaved={loadCandidates} />

      {data.error && <div className="error" style={{ marginBottom: 12 }}>{data.error}</div>}

      {candidates.length === 0 ? (
        <div className="empty-state">Кандидатов нет — все акки оправдывают просмотры.</div>
      ) : (
        <AccountsTable
          accounts={filtered}
          me={me}
          phones={data.phones}
          models={data.models}
          tableKey="shutdown"
          onPatched={data.applyPatched}
          onDeleted={data.applyDeleted}
          onOpen={(a) => nav(`/account/${a.id}`)}
          onError={data.setError}
          rowActions={(a) => (
            <>
              <button
                className="row-open-btn"
                title="Подтвердить: статус «Выключение»"
                disabled={busyId === a.id}
                style={{ color: '#4ade80' }}
                onClick={() => confirm(a.id)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
              </button>
              <button
                className="row-open-btn"
                title="Скрыть из списка на 24 часа"
                disabled={busyId === a.id}
                style={{ color: 'var(--accent)' }}
                onClick={() => snooze(a.id)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
              </button>
            </>
          )}
          toolbar={
            <AccountFilters
              value={filters}
              onChange={setFilters}
              accounts={candidates}
              models={data.models}
              phones={data.phones}
            />
          }
        />
      )}

      <PhoneTabs
        phones={data.phones}
        activeId={filters.phoneIds.length === 1 ? filters.phoneIds[0]! : null}
        onSelect={(id) => setFilters({ ...filters, phoneIds: id ? [id] : [] })}
      />
    </div>
  );
}
